import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, SevPill, StatusPill, SectionTitle, TimeAgo, Kpi } from "@/components/sov";
import { ShieldAlert, ChevronRight, X, FileText, Lock } from "lucide-react";

const STATUS_FLOW = ["triage", "investigation", "containment", "eradication", "recovery", "closed"];

const IncidentDetail = ({ incident, onClose, onUpdated }) => {
  const [advancing, setAdvancing] = useState(false);

  const advance = async (newStatus) => {
    setAdvancing(true);
    try {
      await api.patch(`/incidents/${incident.case_id}/status`, { status: newStatus });
      onUpdated();
    } finally {
      setAdvancing(false);
    }
  };

  if (!incident) return null;
  const currentIdx = STATUS_FLOW.indexOf(incident.status);
  const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentIdx + 1] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end" data-testid="incident-detail">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="sov-panel sov-corners relative h-full w-full max-w-[640px] overflow-y-auto" style={{ background: "var(--obsidian-2)" }}>
        <span className="corner tl" /><span className="corner tr" />
        <span className="corner bl" /><span className="corner br" />
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-[color:var(--gold-line)] bg-[var(--obsidian-2)] z-10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-gold-300">{incident.case_id}</div>
            <h3 className="font-display text-[16px] tracking-[0.12em] text-gold-100 mt-1">
              {incident.title}
            </h3>
          </div>
          <button onClick={onClose} className="text-ivory-3 hover:text-gold-200" data-testid="close-incident">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <SevPill severity={incident.severity} />
            <StatusPill status={incident.status} />
            <span className="font-mono text-[10px] text-ivory-3 ml-auto">
              opened <TimeAgo iso={incident.opened_at} />
            </span>
          </div>

          <div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-2">Summary</div>
            <p className="font-serif text-[14px] text-ivory-2 leading-relaxed italic">
              "{incident.summary}"
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-1">Lead Analyst</div>
              <div className="font-serif text-[15px] text-gold-100">{incident.lead_analyst}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-1">MTTR</div>
              <div className="font-serif text-[15px] text-gold-100">
                {incident.mttr_minutes
                  ? `${Math.floor(incident.mttr_minutes / 60)}h ${incident.mttr_minutes % 60}m`
                  : "in flight"}
              </div>
            </div>
          </div>

          <div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-2">Affected Assets</div>
            <div className="flex flex-wrap gap-1.5">
              {incident.affected_assets.map((a) => (
                <span key={a} className="font-mono text-[10px] px-2 py-1 border border-[color:var(--gold-line)] text-gold-200 tracking-[0.1em]">
                  {a}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-2">MITRE Tactics</div>
            <div className="flex flex-wrap gap-1.5">
              {incident.mitre_tactics.map((t) => (
                <span key={t} className="font-mono text-[10px] px-2 py-1 border border-[color:var(--gold-line)] bg-[rgba(200,162,75,0.06)] text-gold-100 tracking-[0.1em]">
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-3">IR Lifecycle</div>
            <div className="flex items-center gap-1">
              {STATUS_FLOW.map((s, i) => {
                const passed = i <= currentIdx;
                const active = i === currentIdx;
                return (
                  <React.Fragment key={s}>
                    <div className="flex flex-col items-center min-w-[60px]">
                      <div
                        className={`w-3 h-3 rounded-full transition-all ${active ? "scale-125" : ""}`}
                        style={{
                          background: passed ? "#dcb95f" : "rgba(200,162,75,0.15)",
                          boxShadow: active ? "0 0 12px #dcb95f" : "none",
                        }}
                      />
                      <div className={`font-mono text-[8.5px] tracking-[0.14em] uppercase mt-1.5 ${passed ? "text-gold-100" : "text-ivory-3"}`}>
                        {s}
                      </div>
                    </div>
                    {i < STATUS_FLOW.length - 1 && (
                      <div className="flex-1 h-px" style={{ background: i < currentIdx ? "#dcb95f" : "rgba(200,162,75,0.15)" }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Action */}
          {incident.status !== "closed" && nextStatus && (
            <div className="flex gap-3 pt-4 border-t border-[color:var(--gold-line)]">
              <button
                onClick={() => advance(nextStatus)}
                disabled={advancing}
                className="btn-gold flex-1 justify-center"
                data-testid="advance-incident-btn"
              >
                <ChevronRight size={12} />
                Advance to {nextStatus}
              </button>
              <button
                onClick={() => advance("closed")}
                disabled={advancing}
                className="btn-ghost"
                data-testid="close-incident-btn"
              >
                <Lock size={11} />
                Close Case
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Incidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/incidents");
      setIncidents(data.incidents);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const open = incidents.filter((i) => i.status !== "closed").length;
  const critical = incidents.filter((i) => i.severity === "critical").length;
  const closed = incidents.filter((i) => i.status === "closed").length;
  const avgMttr = (() => {
    const c = incidents.filter((i) => i.mttr_minutes);
    if (!c.length) return 0;
    return Math.round(c.reduce((a, i) => a + i.mttr_minutes, 0) / c.length);
  })();

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <SectionTitle sub="// IR LIFECYCLE & CASE LEDGER">
        Incidents
      </SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Kpi testid="kpi-open" label="In-Flight" value={open} sub="active cases" accent />
        <Kpi testid="kpi-critical-inc" label="Critical" value={critical} sub="cardinal severity" />
        <Kpi testid="kpi-closed" label="Closed (lifetime)" value={closed} />
        <Kpi
          testid="kpi-mttr"
          label="Avg MTTR"
          value={`${Math.floor(avgMttr / 60)}h${avgMttr % 60}`}
          sub="mean response"
        />
      </div>

      <Panel
        testid="panel-incidents-table"
        title="Case Ledger"
        code="§ IV.A"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <ShieldAlert size={11} className="text-gold-300" />
            {incidents.length} cases
          </span>
        }
      >
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12"><span className="dot-loader"><span /><span /><span /></span></div>
          ) : (
            <table className="sov-table">
              <thead>
                <tr>
                  <th>Case ID</th>
                  <th>Title</th>
                  <th>Severity</th>
                  <th>Phase</th>
                  <th>Lead</th>
                  <th>Opened</th>
                  <th>MTTR</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((i) => (
                  <tr
                    key={i.id}
                    className="cursor-pointer"
                    onClick={() => setSelected(i)}
                    data-testid={`incident-row-${i.case_id}`}
                  >
                    <td className="text-gold-300">{i.case_id}</td>
                    <td className="text-ivory">{i.title}</td>
                    <td><SevPill severity={i.severity} /></td>
                    <td><StatusPill status={i.status} /></td>
                    <td className="text-ivory-2">{i.lead_analyst}</td>
                    <td><TimeAgo iso={i.opened_at} /></td>
                    <td className="text-ivory-2">
                      {i.mttr_minutes ? `${Math.floor(i.mttr_minutes / 60)}h${i.mttr_minutes % 60}` : "—"}
                    </td>
                    <td><ChevronRight size={14} className="text-gold-300" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>

      {selected && (
        <IncidentDetail
          incident={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { load(); setSelected(null); }}
        />
      )}
    </div>
  );
};

export default Incidents;
